/**
 * @fileoverview "Practice recordings" on the instructor's student page (iOS
 * review B5): the student's rehearsals, newest first, each one a row that
 * opens its own page for feedback.
 *
 * Shown only while the server's rehearsal flag is on (the page decides and
 * passes `enabled`); with it off, nothing is fetched. A failed or empty list
 * renders nothing, like the other cards on the page.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { practiceRecordingMeta, practiceRecordingState } from "@/lib/practiceRecordings";
import { Card, ListRow, SectionHeader, Tag } from "@/components/studio/primitives";

const QUIET_LABELS = {
  processing: "Processing…",
  feedback_given: "Feedback given",
};

/**
 * The row's state. Only a recording waiting on the instructor is an accent
 * pill; the rest are a quiet word, so the one that needs them stands out.
 */
export function PracticeRecordingStateLabel({ recording }) {
  const state = practiceRecordingState(recording);
  if (state === "needs_feedback") {
    return (
      <Tag accent className="shrink-0" data-testid="practice-state">
        Needs feedback
      </Tag>
    );
  }
  return (
    <span className="shrink-0 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="practice-state">
      {QUIET_LABELS[state]}
    </span>
  );
}

export function StudentPracticeRecordingsCard({ studentId, enabled }) {
  const navigate = useNavigate();
  const [recordings, setRecordings] = useState([]);

  useEffect(() => {
    if (!enabled || !studentId) {
      setRecordings([]);
      return undefined;
    }
    const controller = new AbortController();
    api
      .get(`/students/${studentId}/practice-recordings`, { signal: controller.signal })
      .then((res) => setRecordings(Array.isArray(res.data) ? res.data : []))
      .catch(() => {
        if (!controller.signal.aborted) setRecordings([]);
      });
    return () => controller.abort();
  }, [studentId, enabled]);

  if (!enabled || recordings.length === 0) return null;

  return (
    <div data-testid="student-practice-recordings">
      <SectionHeader>Practice recordings</SectionHeader>
      <Card padding={6}>
        {recordings.map((rec, i) => (
          <ListRow
            key={rec.recording_id}
            data-testid={`practice-recording-${rec.recording_id}`}
            title={rec.title || "Practice recording"}
            subtitle={practiceRecordingMeta(rec)}
            trailing={<PracticeRecordingStateLabel recording={rec} />}
            borderBottom={i < recordings.length - 1}
            onPress={() => navigate(`/students/${studentId}/practice/${rec.recording_id}`)}
          />
        ))}
      </Card>
    </div>
  );
}
